import React from 'react';
import { Input, DatePicker, Row, Col, Divider, Select, Radio, Checkbox, Typography } from 'antd';
import dayjs from 'dayjs';
import type { FH110 } from '../../../services/pwh205Api';
import { fmt, PAYMENT_TYPE_OPTIONS } from '../helpers';
import { AcceptanceFieldHint, pwh205f1AcceptanceManifest } from '../../../features/acceptance';

interface Tab1Props {
  formData: Partial<FH110>;
  isEditing: boolean;
  netAmount: number;
  totalAmount: number;
  companyName: string;
  payeeName: string;
  deptName: string;
  currencyName: string;
  documentKey?: string;
  onFieldChange: <K extends keyof FH110>(field: K, value: FH110[K]) => void;
  onCompanyBlur: () => void;
  onPayeeBlur: () => void;
  onDeptBlur: () => void;
  onCurrencyBlur: () => void;
}

const Tab1Header: React.FC<Tab1Props> = ({
  formData, isEditing, netAmount, totalAmount,
  companyName, payeeName, deptName, currencyName,
  documentKey,
  onFieldChange, onCompanyBlur, onPayeeBlur, onDeptBlur, onCurrencyBlur,
}) => {
  // BR-F16: cheque fields enabled only when payment type = 03
  const isCheque = formData.fh110_27 === '03';
  const H = (fieldId: string) => (
    <AcceptanceFieldHint
      manifest={pwh205f1AcceptanceManifest}
      fieldId={fieldId}
      documentKey={documentKey}
    />
  );

  return (
    <>
      {/* Row 1: ID + Date + Company + Payee + Dept + Category */}
      <Row gutter={[16, 8]}>
        <Col span={4}>
          <div className="field-row">
            <label>請款單號{H('fh110_01')}</label>
            <Input value={formData.fh110_01 || '(新增後自動產生)'} readOnly disabled />
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>請款日期{H('fh110_11')}</label>
            <DatePicker
              value={formData.fh110_11 ? dayjs(formData.fh110_11) : null}
              disabled={!isEditing} style={{ width: '100%' }}
              onChange={d => onFieldChange('fh110_11', d ? d.toISOString() : null as unknown as string)}
            />
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>公司代號{H('fh110_16')}</label>
            <div className="lookup-field">
              <Input value={formData.fh110_16 || ''} disabled={!isEditing} maxLength={12}
                onChange={e => onFieldChange('fh110_16', e.target.value)} onBlur={onCompanyBlur} />
              <span className="lookup-name">{companyName}</span>
            </div>
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>受款人代號{H('fh110_15')}</label>
            <div className="lookup-field">
              <Input value={formData.fh110_15 || ''} disabled={!isEditing} maxLength={12}
                onChange={e => onFieldChange('fh110_15', e.target.value)} onBlur={onPayeeBlur} />
              <span className="lookup-name">{payeeName}</span>
            </div>
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>歸屬部門{H('fh110_37')}</label>
            <div className="lookup-field">
              <Input value={formData.fh110_37 || ''} disabled={!isEditing} maxLength={12}
                onChange={e => onFieldChange('fh110_37', e.target.value)} onBlur={onDeptBlur} />
              <span className="lookup-name">{deptName}</span>
            </div>
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>暫借款類別{H('fh110_50')}</label>
            <Radio.Group value={formData.fh110_50} disabled={!isEditing}
              onChange={e => onFieldChange('fh110_50', e.target.value)}>
              <Radio value={1}>貨款</Radio>
              <Radio value={2}>費用</Radio>
            </Radio.Group>
          </div>
        </Col>
      </Row>

      {/* Row 2: Currency + Rate + PaymentType + Dates + Other */}
      <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
        <Col span={3}>
          <div className="field-row">
            <label>幣別{H('fh110_25')}</label>
            <div className="lookup-field">
              <Input value={formData.fh110_25 || ''} disabled={!isEditing} maxLength={6}
                onChange={e => onFieldChange('fh110_25', e.target.value)} onBlur={onCurrencyBlur} />
              <span className="lookup-name">{currencyName}</span>
            </div>
          </div>
        </Col>
        <Col span={3}>
          <div className="field-row">
            <label>匯率{H('fh110_26')}</label>
            <Input value={formData.fh110_26 ?? ''} disabled={!isEditing} type="number"
              onChange={e => onFieldChange('fh110_26', parseFloat(e.target.value) || 0)} />
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>票款別{H('fh110_27')}</label>
            <Select value={formData.fh110_27 || undefined} disabled={!isEditing}
              style={{ width: '100%' }} options={PAYMENT_TYPE_OPTIONS}
              onChange={v => onFieldChange('fh110_27', v)} placeholder="請選擇" />
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>需要日期{H('fh110_23')}</label>
            <DatePicker value={formData.fh110_23 ? dayjs(formData.fh110_23) : null}
              disabled={!isEditing} style={{ width: '100%' }}
              onChange={d => onFieldChange('fh110_23', d ? d.toISOString() : null as unknown as string)} />
          </div>
        </Col>
        <Col span={4}>
          <div className="field-row">
            <label>預計沖銷日{H('fh110_29')}</label>
            <DatePicker value={formData.fh110_29 ? dayjs(formData.fh110_29) : null}
              disabled={!isEditing} style={{ width: '100%' }}
              onChange={d => onFieldChange('fh110_29', d ? d.toISOString() : null as unknown as string)} />
          </div>
        </Col>
        <Col span={3}>
          <div className="field-row">
            <label>其他費用{H('fh110_28')}</label>
            <Input value={formData.fh110_28 ?? ''} disabled={!isEditing}
              type="number" style={{ textAlign: 'right' }}
              onChange={e => onFieldChange('fh110_28', parseFloat(e.target.value) || 0)} />
          </div>
        </Col>
        <Col span={3}>
          <div className="field-row">
            <label>請款淨額{H('fh110_12')}</label>
            <Input value={fmt(netAmount)} readOnly disabled style={{ textAlign: 'right' }} />
          </div>
        </Col>
      </Row>

      {/* Row 3: Totals + Checkbox */}
      <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
        <Col span={4}>
          <div className="field-row">
            <label>請款總額{H('fh110_13')}</label>
            <Input value={fmt(totalAmount)} readOnly disabled style={{ textAlign: 'right' }} />
          </div>
        </Col>
        <Col span={4} className="check-section">
          <div className="field-row" style={{ marginTop: 20 }}>
            <Checkbox checked={!!formData.fh110_242} disabled={!isEditing}
              onChange={e => onFieldChange('fh110_242', e.target.checked)}>
              取消禁背{H('fh110_242')}
            </Checkbox>
          </div>
        </Col>
        <Col span={12}>
          <div className="field-row">
            <label>取消禁背理由{H('fh110_43')}</label>
            <Input.TextArea value={formData.fh110_43 || ''}
              readOnly={!isEditing} disabled={!isEditing || !formData.fh110_242}
              rows={2} onChange={e => onFieldChange('fh110_43', e.target.value)} />
          </div>
        </Col>
      </Row>

      {/* Cheque section: only enabled when payment type = 03 */}
      <div className={`cheque-section ${!isCheque ? 'disabled' : ''}`}>
        <Typography.Text strong style={{ marginBottom: 8, display: 'block' }}>支票資訊</Typography.Text>
        <Row gutter={[16, 8]}>
          <Col span={4}>
            <div className="field-row">
              <label>支票號碼(1){H('fh110_46')}</label>
              <Input value={formData.fh110_46 || ''} disabled={!isEditing || !isCheque}
                onChange={e => onFieldChange('fh110_46', e.target.value)} />
            </div>
          </Col>
          <Col span={4}>
            <div className="field-row">
              <label>支票到期日(1){H('fh110_19')}</label>
              <DatePicker value={formData.fh110_19 ? dayjs(formData.fh110_19) : null}
                disabled={!isEditing || !isCheque} style={{ width: '100%' }}
                onChange={d => onFieldChange('fh110_19', d ? d.toISOString() : null as unknown as string)} />
            </div>
          </Col>
          <Col span={4}>
            <div className="field-row">
              <label>票面金額(1){H('fh110_31')}</label>
              <Input value={formData.fh110_31 ?? ''} disabled={!isEditing || !isCheque}
                type="number" style={{ textAlign: 'right' }}
                onChange={e => onFieldChange('fh110_31', parseFloat(e.target.value) || 0)} />
            </div>
          </Col>
          <Col span={4}>
            <div className="field-row">
              <label>支票號碼(2){H('fh110_47')}</label>
              <Input value={formData.fh110_47 || ''} disabled={!isEditing || !isCheque}
                onChange={e => onFieldChange('fh110_47', e.target.value)} />
            </div>
          </Col>
          <Col span={4}>
            <div className="field-row">
              <label>支票到期日(2){H('fh110_30')}</label>
              <DatePicker value={formData.fh110_30 ? dayjs(formData.fh110_30) : null}
                disabled={!isEditing || !isCheque} style={{ width: '100%' }}
                onChange={d => onFieldChange('fh110_30', d ? d.toISOString() : null as unknown as string)} />
            </div>
          </Col>
          <Col span={4}>
            <div className="field-row">
              <label>票面金額(2){H('fh110_32')}</label>
              <Input value={formData.fh110_32 ?? ''} disabled={!isEditing || !isCheque}
                type="number" style={{ textAlign: 'right' }}
                onChange={e => onFieldChange('fh110_32', parseFloat(e.target.value) || 0)} />
            </div>
          </Col>
        </Row>
      </div>

      {/* Hidden field: fh110_40 (BR-F29, disabled, not visible) */}
      <Input type="hidden" value={formData.fh110_40 || ''} disabled />

      <Divider />

      {/* Remark */}
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <div className="field-row">
            <label>請款說明{H('fh110_17')}</label>
            <Input.TextArea value={formData.fh110_17 || ''}
              readOnly={!isEditing} disabled={!isEditing} rows={3}
              onChange={e => onFieldChange('fh110_17', e.target.value)} />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default Tab1Header;
